import { action, observable, makeObservable, computed } from 'mobx';
import { Constraint, validate } from '../validate';
import { ChangeEvent } from 'react';

export class FormField {
  public value: string = '';
  public originalValue: string;
  public isDirty: boolean = false;
  public isTouched: boolean = false;
  public isFocused: boolean = false;
  public errors: (string | undefined)[] | boolean = false;
  public pasteCopyKeyPressed: boolean = false;
  public inputType: string;
  public serverError: React.ReactNode = '';
  public isWarning: boolean = false;
  private validator: (str: string) => (string | undefined)[] | boolean;

  constructor(
    value: string = '',
    constraints?: Constraint[],
    inputType: string = 'text'
  ) {
    this.value = value;
    this.originalValue = value;
    this.inputType = inputType;
    if (value) {
      this.isDirty = true;
    }

    if (constraints) {
      this.validator = validate(constraints);
    } else {
      // We use dumb validator in case there is no need for validation.
      this.validator = () => false;
    }
    makeObservable(this, {
      value: observable,
      originalValue: observable,
      isDirty: observable,
      isTouched: observable,
      errors: observable,
      serverError: observable,
      inputType: observable,
      isWarning: observable,
      pasteCopyKeyPressed: observable,
      clear: action,
      setPasteCopyKeyPressed: action,
      errorInfo: computed,
      onFocus: action,
      onBlur: action.bound,
      onChange: action.bound,
      addError: action,
      isValid: computed,
    });
  }

  public triggerValidator() {
    this.errors = this.validator(this.value);
  }

  public setValue(value: string) {
    this.value = value;
    this.isDirty = true;
    this.isTouched = true;
  }

  public onChange(e: ChangeEvent<HTMLInputElement>) {
    this.value = e.target.value;
    if (this.value) {
      this.isDirty = true;
    }
    this.serverError = '';
    this.triggerValidator();
  }

  public addError(error: string) {
    if (!Array.isArray(this.errors)) {
      this.errors = [];
    }

    this.errors = [error, ...this.errors];
  }

  get errorInfo() {
    if (!Array.isArray(this.errors)) {
      return '';
    }

    return this.errors[0];
  }

  public clear() {
    this.value = '';
    this.isDirty = false;
    this.isTouched = false;
    this.isFocused = false;
    this.isWarning = false;
  }

  public onBlur() {
    this.isTouched = true;
    this.isFocused = false;
  }

  public onFocus = () => {
    this.isFocused = true;
  };

  public clearErrors = () => {
    this.errors = false;
  };

  get isValid(): boolean {
    return !this.errors;
  }

  public successfullySaved() {
    this.originalValue = this.value;
  }

  get isChanged(): boolean {
    return this.originalValue !== this.value;
  }

  public markFieldAsDirty() {
    this.isDirty = true;
    this.isTouched = true;
  }

  public setConstraints(constraints: Constraint[]) {
    this.validator = validate(constraints);
  }

  public removeConstraints() {
    this.validator = () => false;
  }

  public backToOriginal() {
    this.value = this.originalValue;
  }

  setPasteCopyKeyPressed(status: boolean) {
    this.pasteCopyKeyPressed = status;
  }
}
